import { SortType } from "../SortType";

export enum WalletType {
  CUSTOMER = "CUSTOMER",
  END_USER = "END_USER",
}

export interface WalletApiRow {
  id: number;
  balance: number;
  wallet_type: WalletType;
  customer_id: number;
  customer_name: string;
  user_id: number;
  user_name: string;
  created_date: Date | string;
}

export interface WalletListQuery {
  search?: string;
  wallet_type: WalletType;
  page_no: number;
  limit: number;
  sort_type: SortType;
}

export interface WalletListQueryInput {
  search?: string;
  wallet_type?: string;
  page_no?: number | string;
  limit?: number | string;
  sort_type?: string;
}

const MAX_LIMIT = 200;
const DEFAULT_LIMIT = 30;
const DEFAULT_PAGE_NO = 1;
const DEFAULT_SORT_TYPE = SortType.DESC;

export const DEFAULT_WALLET_LIST_QUERY: WalletListQuery = {
  wallet_type: WalletType.CUSTOMER,
  page_no: DEFAULT_PAGE_NO,
  limit: DEFAULT_LIMIT,
  sort_type: DEFAULT_SORT_TYPE,
};

const toPositiveInt = (
  value: unknown,
  fallback: number,
  max?: number,
): number => {
  const parsed =
    typeof value === "string" ? parseInt(value, 10) : Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) {
    return fallback;
  }
  return max !== undefined ? Math.min(parsed, max) : parsed;
};

const toSortType = (value: unknown): SortType => {
  const upper = typeof value === "string" ? value.toUpperCase() : value;
  return upper === SortType.ASC || upper === SortType.DESC
    ? (upper as SortType)
    : DEFAULT_SORT_TYPE;
};

const toWalletType = (value: unknown): WalletType => {
  const upper = typeof value === "string" ? value.toUpperCase() : value;
  return upper === WalletType.END_USER
    ? WalletType.END_USER
    : WalletType.CUSTOMER;
};

/**
 * Mirrors the backend's walletListQuerySchema (search trim, page_no/limit
 * coercion + clamping, sort_type uppercased) without a schema-validation
 * dependency, matching this repo's hand-rolled validation convention.
 */
export const parseWalletListQuery = (
  input: WalletListQueryInput,
): WalletListQuery => {
  const search = input.search?.trim();
  return {
    ...(search ? { search } : {}),
    wallet_type: toWalletType(input.wallet_type),
    page_no: toPositiveInt(input.page_no, DEFAULT_PAGE_NO),
    limit: toPositiveInt(input.limit, DEFAULT_LIMIT, MAX_LIMIT),
    sort_type: toSortType(input.sort_type),
  };
};

export const walletListQueryToSearchParams = (
  query: WalletListQuery,
): URLSearchParams => {
  const params = new URLSearchParams();
  if (query.search) {
    params.set("search", query.search);
  }
  params.set("wallet_type", query.wallet_type);
  params.set("page_no", String(query.page_no));
  params.set("limit", String(query.limit));
  params.set("sort_type", query.sort_type);
  return params;
};
